import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import { EntriEvents, EntriFlowStatus, EntriSteps, parseDomain } from '@wix/domains-serverless-interface';
import { fetchWithFallback, QueryConfigTypes } from '@wix/premium-fed-sdk/@apis-infra';
import { createApiClient } from '@wix/premium-fed-sdk/@apis-infra-react';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { EntriModule } from '../EntriModule';
import { createModuleExtension } from '@wix/premium-fed-sdk/@premium-module';
import { mountEntriContainer, unmountEntriContainer } from '../entriFullPageContainer';
const NoopService = {
  baseUrl: ''
};
export const EntriWindowClientConfig = {
  entriInjectScript: {
    name: 'entriInjectScript',
    service: NoopService,
    queryConfigType: QueryConfigTypes.FIXED_DATA,
    reactQueryOptions: {
      refetchOnWindowFocus: false,
      refetchOnMount: false
    },
    queryFn: async () => {
      if (window.entri) {
        return {
          success: true
        };
      }
      return new Promise((resolve, reject) => {
        const entriScript = document.createElement('script');
        entriScript.src = 'https://cdn.goentri.com/entri.js';
        entriScript.onload = () => resolve({
          success: true
        });
        entriScript.onerror = evt => reject(evt.error);
        entriScript.type = 'text/javascript';
        entriScript.async = true;
        document.body.appendChild(entriScript);
      });
    }
  },
  entriStart: {
    name: 'entriStart',
    service: NoopService,
    queryConfigType: QueryConfigTypes.REALTIME_NO_REFETCH,
    queryFn: async _ref => {
      var _checkDomainData$NSSu, _exitDetails$error;
      let httpClient = _ref.httpClient,
        _ref$request = _ref.request,
        domain = _ref$request.domain,
        whiteLabelConfig = _ref$request.whiteLabelConfig,
        msid = _ref$request.msid,
        skipConnection = _ref$request.skipConnection,
        entriConfigOverride = _ref$request.entriConfigOverride,
        _ref$request$disableP = _ref$request.disablePointing,
        disablePointing = _ref$request$disableP === void 0 ? false : _ref$request$disableP,
        _ref$request$fullPage = _ref$request.fullPage,
        fullPage = _ref$request$fullPage === void 0 ? false : _ref$request$fullPage;
      const parsedDomain = domain.input ? domain : parseDomain(domain);
      const domainName = parsedDomain.fullAsciiDomain;
      const _DomainsApiClient$que = DomainsApiClient.queries({
          httpClient
        }),
        entriConfig = _DomainsApiClient$que.entriConfig,
        connectAndBindDomain = _DomainsApiClient$que.connectAndBindDomain,
        preCreateConnectedDomain = _DomainsApiClient$que.preCreateConnectedDomain;
      let entriConfigResponse = entriConfigOverride;
      if (!entriConfigOverride) {
        entriConfigResponse = await entriConfig({
          domain,
          disablePointing
        });
      }
      const _ref2 = entriConfigOverride || entriConfigResponse,
        checkDomainData = _ref2.checkDomainData,
        config = _ref2.config,
        isEligible = _ref2.isEligible;
      if (!isEligible || !config) {
        return {
          status: EntriFlowStatus.manual
        };
      }
      await EntriWindowClient.query.entriInjectScript({
        httpClient,
        request: {}
      });
      const connectionTypeFromCheckData = !!parsedDomain.subdomain || !(checkDomainData != null && (_checkDomainData$NSSu = checkDomainData.NSSupport) != null && _checkDomainData$NSSu.root) ? 'pointing' : 'dns';
      const connectionTypeFromConditionalKey = {
        domain: 'pointing',
        subDomain: 'pointing',
        rootNS: 'dns'
      };
      const connectTheDomain = async connectionType => {
        return connectAndBindDomain({
          domain,
          msid,
          connectionType,
          connectedByEntri: true
        });
      };

      // create zone file before allowing external connection process //
      await fetchWithFallback(preCreateConnectedDomain({
        domain: parsedDomain.asciiDomain
      }), {});
      let wasDomainCreated = false;
      const exitDetails = await startEntriClient({
        config,
        whiteLabelConfig,
        fullPage,
        onStepChanged: async (step, provider, conditionalRecordsKey, error) => {
          EntriModule.internals.bi.onEntriStepChange == null || EntriModule.internals.bi.onEntriStepChange({
            step,
            domainName,
            msid,
            provider,
            error
          });
          if (step === EntriSteps.FINISHED_SUCCESSFULLY) {
            if (!wasDomainCreated && !skipConnection) {
              wasDomainCreated = true;
              const connectionType = conditionalRecordsKey ? connectionTypeFromConditionalKey[conditionalRecordsKey] : connectionTypeFromCheckData;
              connectTheDomain(connectionType);
            }
          }
        }
      });
      const error = !exitDetails ? 'no entri.exitDetails' : (_exitDetails$error = exitDetails.error) == null ? void 0 : _exitDetails$error.code;
      const commonRes = {
        checkDomainData,
        config,
        exitDetails,
        error
      };
      switch (true) {
        case exitDetails.success:
          return {
            ...commonRes,
            status: EntriFlowStatus.success
          };
        case exitDetails.setupType === 'manual':
          return {
            ...commonRes,
            status: EntriFlowStatus.manual
          };
        // TODO: (Omer) handle specific EntriCloseStatues according to requirements
        case exitDetails.lastStatus === EntriSteps.EXIT_WITH_ERROR:
        case !!exitDetails.error:
        case exitDetails.lastStatus === EntriSteps.IN_PROGRESS:
        default:
          return {
            ...commonRes,
            status: EntriFlowStatus.abort
          };
      }
    }
  }
};
export const EntriWindowClient = createApiClient(EntriWindowClientConfig);
const Provider = EntriWindowClient.Provider,
  backend = _objectWithoutPropertiesLoose(EntriWindowClient, ["Provider"]);
export const EntriWindowClientExtension = createModuleExtension({
  extensionName: 'entriBackend',
  internals: {
    backend
  },
  Provider
});
function startEntriClient(_ref3) {
  let config = _ref3.config,
    whiteLabelConfig = _ref3.whiteLabelConfig,
    _ref3$fullPage = _ref3.fullPage,
    fullPage = _ref3$fullPage === void 0 ? false : _ref3$fullPage,
    onStepChanged = _ref3.onStepChanged;
  return new Promise(resolve => {
    let manualSetupClicked = false;
    const handleEntriStepChanged = async evt => {
      const data = evt.detail;
      if (data.step === EntriSteps.MANUAL_CONFIGURATION) {
        manualSetupClicked = true;
        await window.entri.close();
      } else {
        var _data$conditionalReco;
        onStepChanged == null || onStepChanged(data.step, data.provider, (_data$conditionalReco = data.conditionalRecords) == null ? void 0 : _data$conditionalReco.key, data.error ? JSON.stringify(data.error) : undefined);
      }
    };
    const handleClose = evt => {
      disposeListeners();
      if (fullPage) {
        unmountEntriContainer();
      }
      const result = evt.detail;
      if (result != null && result.manualScreenDisabled) {
        manualSetupClicked = true;
      }
      resolve({
        ...result,
        setupType: manualSetupClicked ? 'manual' : result.setupType
      });
    };
    const _addEventsListeners = addEventsListeners({
        [EntriEvents.onEntriStepChange]: handleEntriStepChanged,
        [EntriEvents.onEntriClose]: handleClose
      }),
      disposeListeners = _addEventsListeners.dispose;
    if (fullPage) {
      mountEntriContainer();
    }
    window.entri.showEntri({
      ...whiteLabelConfig,
      ...config
    });
  });
}
const addEventsListeners = eventsMap => {
  Object.entries(eventsMap).forEach(_ref4 => {
    let event = _ref4[0],
      handler = _ref4[1];
    window.addEventListener(event, handler, false);
  });
  const dispose = () => {
    Object.entries(eventsMap).forEach(_ref5 => {
      let event = _ref5[0],
        handler = _ref5[1];
      window.removeEventListener(event, handler);
    });
  };
  return {
    dispose
  };
};
//# sourceMappingURL=backend.js.map